import React, { useEffect, useState } from 'react';
import { getSales, voidSale } from '../constants/api';
import { useAuth } from '../constants/AuthContext';
import { COLORS } from '../constants/theme';

export default function HistoryPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'Owner';
  const isMobile = window.innerWidth < 768;
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(isOwner ? 'All' : 'Today');
  const [voiding, setVoiding] = useState(false);

  const fetchSales = async () => {
    try {
      const res = await getSales();
      setSales(res.data);
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSales(); }, []);

  const getFiltered = () => {
    const now = new Date();
    let result = sales;
    if (filter === 'Today') result = result.filter(s => new Date(s.date).toDateString() === now.toDateString());
    else if (filter === 'This Week') { const w = new Date(); w.setDate(now.getDate() - 7); result = result.filter(s => new Date(s.date) >= w); }
    else if (filter === 'This Month') result = result.filter(s => { const d = new Date(s.date); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); });
    if (search.trim()) result = result.filter(s => s.productName.toLowerCase().includes(search.toLowerCase()));
    return result;
  };

  const filtered = getFiltered();
  const active = filtered.filter(s => !s.isVoided);
  const voided = filtered.filter(s => s.isVoided);
  const totalSales = active.reduce((sum, s) => sum + s.totalAmount, 0);
  const totalProfit = active.reduce((sum, s) => sum + s.profit, 0);

  const handleVoid = async (sale) => {
    if (!window.confirm('Void this sale? Stock will be restored.')) return;
    setVoiding(true);
    try {
      await voidSale(sale.id);
      await fetchSales();
    } catch (err) {
      alert(err?.response?.data ?? 'Something went wrong.');
    } finally {
      setVoiding(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: COLORS.accent }}>Loading...</div>
  );

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px', maxWidth: 1100, margin: '0 auto' }}>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: isMobile ? 20 : 26, fontWeight: 900, color: COLORS.ink }}>Sales History</div>
        <div style={{ fontSize: 13, color: COLORS.inkLight, marginTop: 4 }}>{isOwner ? 'All transactions' : "Today's transactions"}</div>
      </div>

      {/* Summary */}
      <div style={{ background: COLORS.accentLight, borderRadius: 14, padding: '16px 20px', border: `1px solid #B6DFC7`, marginBottom: 16, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.accent, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>Total Sales</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: COLORS.accent }}>₦{totalSales.toLocaleString()}</div>
        </div>
        {isOwner && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.accent, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>Total Profit</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: COLORS.accent }}>₦{totalProfit.toLocaleString()}</div>
          </div>
        )}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.accent, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>Transactions</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: COLORS.accent }}>{active.length}</div>
        </div>
      </div>

      {/* Search */}
      <input
        type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
        style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1.5px solid ${COLORS.border}`, fontSize: 14, color: COLORS.ink, background: 'white', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 12 }}
        onFocus={e => e.target.style.borderColor = COLORS.accent}
        onBlur={e => e.target.style.borderColor = COLORS.border}
      />

      {/* Filter tabs — owner only */}
      {isOwner && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
          {['All', 'Today', 'This Week', 'This Month'].map(tab => (
            <button key={tab} onClick={() => setFilter(tab)}
              style={{ padding: '8px 16px', borderRadius: 20, border: `1.5px solid ${filter === tab ? COLORS.accent : COLORS.border}`, background: filter === tab ? COLORS.accent : 'white', color: filter === tab ? 'white' : COLORS.inkLight, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              {tab}
            </button>
          ))}
        </div>
      )}

      {/* Sales list */}
      {active.length === 0 && voided.length === 0 ? (
        <div style={{ background: 'white', borderRadius: 16, padding: 48, border: `1px solid ${COLORS.border}`, textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🧾</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.ink }}>{search ? `No results for "${search}"` : 'No sales found'}</div>
        </div>
      ) : (
        <>
          {active.map((sale, i) => (
            <div key={sale.id} style={{ background: 'white', borderRadius: 14, border: `1px solid ${COLORS.border}`, padding: '14px 16px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>{sale.productName}</div>
                <div style={{ fontSize: 12, color: COLORS.inkLight, marginTop: 3 }}>
                  ×{sale.quantitySold} · {new Date(sale.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} · {new Date(sale.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              <div style={{ textAlign: 'right', marginLeft: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>₦{sale.totalAmount.toLocaleString()}</div>
                {isOwner && <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent, marginTop: 2 }}>+₦{sale.profit.toLocaleString()}</div>}
                {isOwner && (
                  <button onClick={() => handleVoid(sale)} disabled={voiding}
                    style={{ marginTop: 6, padding: '4px 10px', borderRadius: 8, border: `1px solid #fca5a5`, background: '#FEE2E2', fontSize: 11, fontWeight: 700, color: COLORS.danger, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Void
                  </button>
                )}
              </div>
            </div>
          ))}

          {voided.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.inkLight, letterSpacing: 1.2, textTransform: 'uppercase', margin: '16px 0 10px' }}>Voided ({voided.length})</div>
              {voided.map(sale => (
                <div key={sale.id} style={{ background: 'white', borderRadius: 14, border: `1px solid ${COLORS.border}`, padding: '14px 16px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', opacity: 0.5 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.inkLight, textDecoration: 'line-through' }}>{sale.productName}</div>
                    <div style={{ fontSize: 12, color: COLORS.inkLight, marginTop: 3 }}>×{sale.quantitySold} · {new Date(sale.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 14, color: COLORS.inkLight, textDecoration: 'line-through' }}>₦{sale.totalAmount.toLocaleString()}</div>
                    <div style={{ background: '#FEE2E2', borderRadius: 20, padding: '3px 8px', fontSize: 10, fontWeight: 700, color: COLORS.danger, marginTop: 4 }}>VOIDED</div>
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}